import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Copy,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { cn } from '@/shared/libs';
import type { Offer } from '@psp-aggregator/sdk';

import {
  getOfferAttributes,
  getOfferDisplayName,
} from '../../libs/offerAttributes';
import { OFFER_LEG_KEYS } from '../../model/legs';
import { CoverageStrip } from '../CoverageStrip/CoverageStrip';
import { GeoBadge } from '../GeoBadge/GeoBadge';
import { LegBlock } from '../LegBlock/LegBlock';

export interface OfferCardProps {
  mode?: 'admin' | 'catalog';
  offer: Offer;
  onCopy?: (offer: Offer) => void;
  onDelete?: (offer: Offer) => void;
  onEdit?: (offer: Offer) => void;
  onOpen?: (offer: Offer) => void;
  onRestore?: (offer: Offer) => void;
  onSelect?: (offer: Offer) => void;
  onToggleStatus?: (offer: Offer) => void;
  paused?: boolean;
  providerName?: string;
  selected?: boolean;
}

const reserveText = (
  reserve: ReturnType<typeof getOfferAttributes>['rolling_reserve'],
): string =>
  [
    reserve.reserve_pct ? `${reserve.reserve_pct}%` : '',
    reserve.hold_days ? `${reserve.hold_days}d` : '',
    reserve.cap_amount,
  ]
    .filter(Boolean)
    .join(' / ');

export const OfferCard: FC<OfferCardProps> = ({
  mode = 'admin',
  offer,
  onCopy,
  onDelete,
  onEdit,
  onOpen,
  onRestore,
  onSelect,
  onToggleStatus,
  paused = false,
  providerName,
  selected = false,
}) => {
  const { t } = useTranslation();
  const attributes = getOfferAttributes(offer);
  const reserve = reserveText(attributes.rolling_reserve);
  const isAdmin = mode === 'admin';
  const title = isAdmin ? getOfferDisplayName(offer) : offer.alias || offer.id;
  const initial = (providerName ?? title).trim().charAt(0).toUpperCase();

  return (
    <div
      className={cn(
        'flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card transition-all duration-300',
        selected
          ? 'border-indigo-500 shadow-lg shadow-indigo-500/15 ring-1 ring-indigo-500/30'
          : 'border-border/60 hover:-translate-y-1 hover:border-indigo-500/30 hover:shadow-lg hover:shadow-indigo-500/5',
        paused && !selected && 'opacity-60',
      )}
      role="button"
      tabIndex={0}
      onClick={(event) => {
        const target = event.target;

        if (target instanceof Element && target.closest('button')) {
          return;
        }

        onOpen?.(offer);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen?.(offer);
        }
      }}>
      <CoverageStrip offer={offer} />
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base leading-tight font-semibold">
              {title}
            </h3>
            {isAdmin && !!providerName && (
              <div className="mt-1 flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center rounded-md bg-indigo-500/15 text-[9px] font-bold text-indigo-600 dark:text-indigo-300">
                  {initial}
                </span>
                <span className="truncate text-xs font-medium">
                  {providerName}
                </span>
              </div>
            )}
            {isAdmin && !!offer.alias && (
              <div className="mt-1.5">
                <span className="inline-flex rounded-md border border-indigo-500/20 bg-indigo-500/10 px-2 py-0.5 font-mono text-[11px] text-indigo-600 dark:text-indigo-300">
                  {offer.alias}
                </span>
              </div>
            )}
          </div>
          <GeoBadge
            prominent
            className="shrink-0 pt-0.5 text-base font-bold tracking-tight uppercase"
            countries={attributes.countries}
            regions={attributes.regions}
          />
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]">
          {attributes.currencies.length > 0 && (
            <span className="font-semibold text-foreground/80">
              {attributes.currencies.join(', ')}
            </span>
          )}
          {!!attributes.traffic_type && (
            <span className="text-muted-foreground">
              · {attributes.traffic_type}
            </span>
          )}
          {attributes.payment_methods.length > 0 && (
            <span className="text-muted-foreground">
              · {attributes.payment_methods.join(', ')}
            </span>
          )}
          {attributes.integration_methods.length > 0 && (
            <span className="text-muted-foreground">
              · {attributes.integration_methods.join(', ')}
            </span>
          )}
          {attributes.restricted_countries.length > 0 && (
            <span className="font-medium text-rose-500">
              · {attributes.restricted_countries.length}
            </span>
          )}
        </div>

        {OFFER_LEG_KEYS.map((leg) => (
          <LegBlock
            key={leg}
            layout="line"
            leg={leg}
            offer={offer}
            showCost={isAdmin}
          />
        ))}

        {(!!reserve || !!attributes.refund || !!attributes.chargeback) && (
          <p className="mt-2 border-t border-border/15 pt-2 text-[11px] text-muted-foreground">
            {!!reserve && <span>RR {reserve}</span>}
            {!!attributes.refund && <span> · {attributes.refund}</span>}
            {!!attributes.chargeback && <span> · {attributes.chargeback}</span>}
          </p>
        )}
        {!!attributes.guarantee_amount && (
          <div className="mt-3 mb-1 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/15 to-teal-500/10 px-3 py-2 shadow-sm shadow-emerald-500/10">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
              {t('pages.admin.offers.fields.guarantee')}{' '}
              {attributes.guarantee_amount}
            </span>
          </div>
        )}

        <div className="mt-auto flex items-center gap-2 border-t border-border/40 pt-3">
          {isAdmin && (
            <div className="flex items-center gap-1.5">
              {!!onEdit && (
                <button
                  aria-label={t('buttons.edit')}
                  className="cursor-pointer rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-indigo-500/10 hover:text-indigo-500"
                  type="button"
                  onClick={() => onEdit(offer)}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {!!onCopy && (
                <button
                  aria-label={t('buttons.copy')}
                  className="cursor-pointer rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-indigo-500/10 hover:text-indigo-500"
                  type="button"
                  onClick={() => onCopy(offer)}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              {!!onToggleStatus && (
                <button
                  aria-label={paused ? t('buttons.resume') : t('buttons.pause')}
                  className={cn(
                    'cursor-pointer rounded-lg p-1.5 transition-colors',
                    paused
                      ? 'text-emerald-500 hover:bg-emerald-500/10'
                      : 'text-amber-500 hover:bg-amber-500/10',
                  )}
                  type="button"
                  onClick={() => onToggleStatus(offer)}>
                  {paused ? (
                    <Play className="h-3.5 w-3.5" />
                  ) : (
                    <Pause className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              {!!onRestore && (
                <button
                  aria-label={t('buttons.restore')}
                  className="cursor-pointer rounded-lg p-1.5 text-emerald-500 transition-colors hover:bg-emerald-500/10"
                  type="button"
                  onClick={() => onRestore(offer)}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}
              {!!onDelete && (
                <button
                  aria-label={t('buttons.delete')}
                  className="cursor-pointer rounded-lg p-1.5 text-rose-500 transition-colors hover:bg-rose-500/10"
                  type="button"
                  onClick={() => onDelete(offer)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <div className="ml-auto">
            {paused ? (
              <span className="inline-flex cursor-not-allowed items-center gap-1 rounded-xl bg-amber-500/10 px-3.5 py-2 text-xs font-medium text-amber-600 dark:text-amber-400">
                <Pause className="h-3.5 w-3.5" />
                {t('status.paused')}
              </span>
            ) : (
              !!onSelect && (
                <button
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-medium transition-all',
                    selected
                      ? 'border border-indigo-500 bg-card text-indigo-600 hover:bg-indigo-500/5 dark:text-indigo-300'
                      : 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5',
                  )}
                  type="button"
                  onClick={() => onSelect(offer)}>
                  {selected ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  {selected
                    ? t('pages.admin.offers.added')
                    : t('pages.admin.offers.add')}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

OfferCard.displayName = 'OfferCard';
